import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  id: string;
  message: string;
  variant?: ToastVariant;
  duration?: number;
  action?: ToastAction;
  onClose: (id: string) => void;
}

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'bg-white border-emerald-200 text-slate-900',
  error: 'bg-white border-red-200 text-slate-900',
  info: 'bg-white border-sky-200 text-slate-900',
  warning: 'bg-white border-amber-200 text-slate-900',
};

const VARIANT_ICON_COLOR: Record<ToastVariant, string> = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  info: 'text-sky-500',
  warning: 'text-amber-500',
};

const VARIANT_ACTION_COLOR: Record<ToastVariant, string> = {
  success: 'text-emerald-700 hover:text-emerald-800',
  error: 'text-red-700 hover:text-red-800',
  info: 'text-sky-700 hover:text-sky-800',
  warning: 'text-amber-700 hover:text-amber-800',
};

const VARIANT_ICONS = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const OVERLAY_SHADOW = 'shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]';

export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  variant = 'info',
  duration = 5000,
  action,
  onClose,
}) => {
  const Icon = VARIANT_ICONS[variant];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => onClose(id), duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  return (
    <div
      className={`${VARIANT_STYLES[variant]} ${OVERLAY_SHADOW} border rounded-xl p-3 mb-2 flex items-start gap-3 animate-slide-in-right max-w-md w-full`}
      role="alert"
    >
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${VARIANT_ICON_COLOR[variant]}`} strokeWidth={2} />

      <div className="flex-1 text-[13px]">
        <div className="text-slate-700">{message}</div>
        {action && (
          <button
            type="button"
            onClick={() => {
              action.onClick();
              onClose(id);
            }}
            className={`mt-1 text-[12px] font-semibold underline ${VARIANT_ACTION_COLOR[variant]}`}
          >
            {action.label}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => onClose(id)}
        className="flex-shrink-0 text-slate-400 hover:text-slate-700 rounded-md p-0.5"
        aria-label="Toast schließen"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
};

export default Toast;
